// "Recently Rated" on a profile, under Top Movies: up to 10 posters with the
// person's stars and a link to their full history. Visitors only receive
// `recentRatings` when the profile is viewable, so an empty or missing list
// simply hides the section; the owner gets a compact empty state instead.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import RatedMovieCard from './RatedMovieCard';
import topStyles from '../TopMovies/TopMovies.module.css';
import styles from './RatedMovies.module.css';

/**
 * items: undefined while loading, null when hidden / unavailable, else array.
 * error + onRetry: the owner's own fetch failed.
 */
export default function RecentlyRatedSection({ items, isOwner = false, seeAllTo, error = null, onRetry, sourceScreen, loggedIn = true, className = '' }) {
    const profileType = isOwner ? 'own' : 'other';
    const viewedRef = useRef(false);
    const count = Array.isArray(items) ? items.length : 0;

    // Once per profile view, only when the section actually shows ratings.
    useEffect(() => {
        if (viewedRef.current || count === 0) return;
        viewedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.RATINGS_PROFILE_VIEWED, { source_screen: sourceScreen, profile_type: profileType, rating_count: count, logged_in: loggedIn });
    }, [count, sourceScreen, profileType, loggedIn]);

    if (error && isOwner) {
        return (
            <section className={`${topStyles.profileCard} ${className}`} aria-label="Recently Rated">
                <span className={topStyles.kicker}>Recently Rated</span>
                <p className={styles.errorText} role="alert">
                    Couldn't load your ratings.{' '}
                    {onRetry && <button type="button" className={styles.linkBtn} onClick={onRetry}>Try again</button>}
                </p>
            </section>
        );
    }
    if (items === undefined || items === null) return null;

    if (count === 0) {
        if (!isOwner) return null;
        return (
            <section className={`${topStyles.profileCard} ${styles.emptyCard} ${className}`} aria-label="Recently Rated">
                <span className={topStyles.kicker}>Recently Rated</span>
                <p className={styles.emptyTitle}>Your movie opinions start here.</p>
                <p className={styles.emptyText}>Rate movies you've watched and they'll appear here.</p>
                <Link to="/discover" className={topStyles.secondary}>Explore Movies</Link>
            </section>
        );
    }

    return (
        <section className={`${topStyles.profileCard} ${className}`} aria-label="Recently Rated">
            <div className={topStyles.profileHead}>
                <h2 className={styles.heading}>Recently Rated</h2>
                {seeAllTo && (
                    <Link
                        to={seeAllTo}
                        className={styles.seeAll}
                        onClick={() => trackEvent(ANALYTICS_EVENTS.RATINGS_SEE_ALL_CLICKED, { source_screen: sourceScreen, profile_type: profileType, logged_in: loggedIn })}
                    >
                        See All Ratings →
                    </Link>
                )}
            </div>
            <ul className={styles.rail}>
                {items.slice(0, 10).map((item) => <RatedMovieCard key={item.tmdbId} item={item} />)}
            </ul>
        </section>
    );
}
